import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";

import type { ObjectDataAccess } from "dataAccess";

export const MAX_PRELOAD_CONCURRENCY = 5;

/**
 * Warms the subtree ids for the objects, but only where the backend can produce them
 * cheaply — {@link ObjectDataAccess.warmDescendantIds}. This feeds the cache that
 * {@link getGroupStateForIdSet} relies on to propagate selection / visibility state up
 * to folder nodes.
 *
 * Best-effort, but **not free on every backend, so the caller sizes the batch**:
 *
 * - The object DB reads its pre-computed descendants file and declines when there is
 *   none, so a miss costs one probe and never the paginated walk
 *   {@link ObjectDataAccess.getDescendantIds} would fall back to.
 * - A served BIM index has no such file: it spends one subtree request and one bitmap
 *   decode per object, and leaves the expanded ids on the reference. Cheap per call
 *   next to a walk, but it is a request, and this runs over whole levels of the tree.
 * - A downloaded BIM index spends one worker call per object against files already on
 *   the device — no request, but a subtree read out of OPFS and a set held in memory.
 *
 * **A `false` does not mean "no descendants"** — only that the ids are not on the
 * object, whether because the backend declined, the read failed, or it was aborted.
 *
 * @returns `true` when at least one object was populated and the batch was not aborted
 */
export async function fetchDescendantsBatch(
    dataAccess: ObjectDataAccess,
    objects: HierarcicalObjectReference[],
    signal: AbortSignal,
): Promise<boolean> {
    let populated = false;
    try {
        await pMap(
            objects,
            async (obj) => {
                if (await dataAccess.warmDescendantIds(obj, signal)) {
                    populated = true;
                }
            },
            { concurrency: MAX_PRELOAD_CONCURRENCY, signal },
        );
    } catch {
        // pMap rejects when the signal aborts; treat as "nothing to show".
        return false;
    }
    return populated && !signal.aborted;
}
